import React, { useEffect } from "react";
import { MdClose } from "react-icons/md";
import { Link } from "react-router-dom";
import Photo from "./Posts/Photo";
import Video from "./Posts/Video";
import Voice from "./Posts/Voice";
import PostInfo from "./PostInfo";
import ReadMore from "./ReadMore";
import { getMediaUrl } from "../lib/utils";

const NotificationPostModal = ({ notification, user, onClose }) => {
  const isOpen = Boolean(notification);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!notification) return null;

  const post = {
    id: notification.post_id,
    caption: notification.caption,
    content: notification.content,
    type: notification.post_type,
    creator_id: notification.creator_id,
    firstname: notification.creator_firstname,
    lastname: notification.creator_lastname,
    profile_img: notification.creator_profile_img,
  };

  const activityNeedsComments = ["comment", "comment_like", "reply"].includes(notification.type);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm sm:p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">

        {/* HEADER */}

        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-5">
          <div>
            <h2 className="font-bold text-gray-900">Post</h2>

            <p className="text-xs text-gray-500">
              <Link to={`/profile/${notification.user_id}`} onClick={onClose} className="font-semibold text-gray-700 hover:text-purple-600 hover:underline">
                {notification.firstname} {notification.lastname}
              </Link>{" "}
              {notification.activity}
            </p>
          </div>

          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition hover:bg-gray-200">
            <MdClose size={23} />
          </button>
        </div>

        {/* SCROLLABLE POST */}

        <div className="overflow-y-auto">
          <div className="p-4 sm:p-5">

            {/* POST CREATOR */}

            <div className="mb-4 flex items-center gap-3">
              <Link to={`/profile/${notification.creator_id}`} onClick={onClose} className="shrink-0">
                <img
                  src={getMediaUrl(notification.creator_profile_img)}
                  alt={`${notification.creator_firstname || ""} ${notification.creator_lastname || ""}`}
                  className="h-11 w-11 rounded-full object-cover transition hover:ring-2 hover:ring-purple-300"
                />
              </Link>

              <div className="min-w-0">
                <Link to={`/profile/${notification.creator_id}`} onClick={onClose} className="font-semibold text-gray-900 transition hover:text-purple-600 hover:underline">
                  {notification.creator_firstname} {notification.creator_lastname}
                </Link>

                <p className="text-xs text-gray-400">Original post</p>
              </div>
            </div>

            {/* CAPTION */}

            {notification.caption && (
              <div className="mb-4 text-[15px] leading-6 text-gray-700">
                <ReadMore text={notification.caption} />
              </div>
            )}

            {/* TEXT */}

            {notification.post_type === "text" && (
              <div className="rounded-2xl bg-gray-50 p-5 text-[16px] leading-7 text-gray-700">
                {notification.content}
              </div>
            )}

            {/* PHOTO */}

            {notification.post_type === "photo" && (
              <div className="overflow-hidden rounded-2xl">
                <Photo content={notification.content} />
              </div>
            )}

            {/* VIDEO */}

            {notification.post_type === "video" && (
              <div className="overflow-hidden rounded-2xl bg-black">
                <Video content={notification.content} />
              </div>
            )}

            {/* VOICE */}

            {notification.post_type === "voice" && (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <Voice url={notification.content} postId={notification.post_id} />
              </div>
            )}

            {/* POST ACTIONS */}

            <div className="mt-5 border-t border-gray-100 pt-4 text-gray-500">
              <PostInfo post={post} user={user} />
            </div>

            {/* CONTEXT */}

            {activityNeedsComments && (
              <div className="mt-4 rounded-xl bg-purple-50 px-4 py-3 text-sm text-purple-700">
                {notification.type === "comment" && "This notification came from a comment on this post. Click the comment button above to see the conversation."}
                {notification.type === "comment_like" && "This notification came from activity on one of your comments. Click the comment button above to see the conversation."}
                {notification.type === "reply" && "This notification came from a reply to one of your comments. Click the comment button above to see the conversation."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPostModal;